import { isReadOnlySession } from '../auth/access';
import { authFetch } from '../auth/authFetch';
import { readDemoSession } from '../auth/demoSession';
import {
  peekWorkspaceIdentity,
  ReadOnlyForbiddenError,
  redirectToSignIn,
  requireIdentityHeaders,
} from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';
import { ApiError } from './apiError';
import { readFileResponse } from './fileResponse';
import {
  CHUNK_UPLOAD_THRESHOLD_BYTES,
  shouldChunkUpload,
  uploadInChunks,
} from './chunkedUpload';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function assertNotReadOnly(method = 'GET') {
  if (
    MUTATING_METHODS.has(String(method || 'GET').toUpperCase()) &&
    isReadOnlySession(readDemoSession())
  ) {
    throw new ReadOnlyForbiddenError();
  }
}

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );

function buildQueryString(query) {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.ORIGINATOR);
}

export function getOriginatorApiConfig() {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.ORIGINATOR);
  return {
    apiBaseUrl: API_BASE_URL,
    userId: identity.userId,
    orgId: identity.orgId,
  };
}

// Generate a key per submit intent; callers reuse it across retries so the
// backend can dedupe on (asset_id, request_type, idempotency_key).
export function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    let payload = null;
    try {
      payload = await response.json();
      message = extractErrorMessage(payload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    const code =
      payload && typeof payload === 'object' && typeof payload.code === 'string'
        ? payload.code
        : null;
    throw new ApiError(message, {
      status: response.status,
      code,
      payload,
    });
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query, headers } = {}) {
  assertNotReadOnly(method);
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

/**
 * PUT raw file bytes through the backend Ingest proxy.
 * Must not send application/json and must not target Azure/SAS URLs.
 */
async function requestRaw(path, { method = 'PUT', body, contentType, headers: extraHeaders } = {}) {
  assertNotReadOnly(method);
  const headers = {
    ...getContextHeaders(),
    ...(extraHeaders || {}),
  };
  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  const response = await authFetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body,
  });
  return parseResponse(response);
}

/**
 * Use the backend-returned upload_url only when it is the Ingest proxy path.
 * Azure blob URLs and SAS query strings are ignored.
 */
export function resolveBackendUploadPath(uploadUrl, packageId, fileId) {
  const fallback = `/v1/packages/${packageId}/files/${fileId}/content`;
  if (!uploadUrl || typeof uploadUrl !== 'string') {
    return fallback;
  }

  const trimmed = uploadUrl.trim();
  if (!trimmed) {
    return fallback;
  }
  if (/[?&]sig=/i.test(trimmed) || /blob\.core\.windows\.net/i.test(trimmed)) {
    return fallback;
  }

  if (trimmed.startsWith('/v1/packages/') && trimmed.endsWith('/content')) {
    return trimmed.split('?')[0];
  }

  try {
    const parsed = new URL(trimmed, API_BASE_URL);
    if (
      parsed.pathname.startsWith('/v1/packages/') &&
      parsed.pathname.endsWith('/content')
    ) {
      return parsed.pathname;
    }
  } catch {
    // Fall through to the known proxy path.
  }

  return fallback;
}

export function getDashboardSummary() {
  return request('/v1/originator/dashboard/summary');
}

export function getDashboardAlerts() {
  return request('/v1/originator/dashboard/alerts');
}

export function listBatches({ page = 1, pageSize = 20, status, programId } = {}) {
  return request('/v1/batches', {
    query: {
      page,
      page_size: pageSize,
      status,
      program_id: programId,
    },
  });
}

export function getBatch(batchId) {
  return request(`/v1/batches/${batchId}`);
}

export function getBatchExceptions(batchId) {
  return request(`/v1/batches/${batchId}/exceptions`);
}

export function supplementBatch(batchId, notes) {
  return request(`/v1/batches/${batchId}/supplement`, {
    method: 'POST',
    body: { notes: notes || null },
  });
}

export function createPackage({ programId, referenceNumber }) {
  return request('/v1/packages', {
    method: 'POST',
    body: {
      program_id: programId,
      reference_number: referenceNumber || null,
    },
  });
}

/** @deprecated Metadata-only registration. Creates PENDING files; cannot complete an upload. */
export function uploadPackageFile(packageId, file) {
  return request(`/v1/packages/${packageId}/files`, {
    method: 'POST',
    body: {
      file_name: file.file_name,
      storage_path: file.storage_path,
      checksum: file.checksum || null,
      mime_type: file.mime_type || null,
      size_bytes: file.size_bytes ?? null,
    },
  });
}

export function createUploadSession(
  packageId,
  { fileName, mimeType, sizeBytes, sourceChannel = 'DEVICE' } = {}
) {
  return request(`/v1/packages/${packageId}/files/upload-session`, {
    method: 'POST',
    body: {
      file_name: fileName,
      mime_type: mimeType || null,
      size_bytes: sizeBytes ?? null,
      source_channel: sourceChannel || 'DEVICE',
    },
  });
}

export function listIngestChannels() {
  return request('/v1/packages/ingest-channels');
}

export function listIngestChannelFiles(channel, { kind } = {}) {
  return request(`/v1/packages/ingest-channels/${encodeURIComponent(channel)}/files`, {
    query: kind ? { kind } : undefined,
  });
}

export function importPackageFiles(packageId, { channel, fileNames } = {}) {
  return request(`/v1/packages/${packageId}/files/import`, {
    method: 'POST',
    body: {
      channel,
      file_names: fileNames,
    },
  });
}

export function getIngestApiDetails() {
  return request('/v1/packages/ingest-channels/api-details');
}

/**
 * Chunked Ingest upload: PUT …/content/chunks/{index} per slice, then
 * POST …/content/chunks/complete. The server hashes the assembled blob, so the
 * usual confirmUpload follows unchanged. See api/chunkedUpload.js.
 *
 * @param {{ contentType?: string, chunkSize?: number, onProgress?: Function, maxAttempts?: number }} [options]
 */
export function uploadFileContentChunked(
  packageId,
  fileId,
  file,
  { contentType, chunkSize, onProgress, maxAttempts } = {}
) {
  const base = `/v1/packages/${packageId}/files/${fileId}/content/chunks`;
  const headers = contentType ? { 'X-Upload-Content-Type': contentType } : undefined;
  return uploadInChunks(file, {
    chunkSize,
    maxAttempts,
    onProgress,
    putChunk: (index, slice) =>
      requestRaw(`${base}/${index}`, {
        method: 'PUT',
        body: slice,
        contentType: 'application/octet-stream',
        headers,
      }),
    complete: (chunkCount) =>
      request(`${base}/complete`, {
        method: 'POST',
        body: { chunk_count: chunkCount },
        headers,
      }),
  });
}

export function uploadFileContent(
  packageId,
  fileId,
  data,
  { uploadUrl, contentType, onProgress, chunkThreshold = CHUNK_UPLOAD_THRESHOLD_BYTES } = {}
) {
  if (shouldChunkUpload(data, chunkThreshold)) {
    return uploadFileContentChunked(packageId, fileId, data, { contentType, onProgress });
  }
  const path = resolveBackendUploadPath(uploadUrl, packageId, fileId);
  return requestRaw(path, {
    method: 'PUT',
    body: data,
    contentType: contentType || undefined,
  });
}

export function confirmUpload(packageId, fileId, payload = {}) {
  return request(`/v1/packages/${packageId}/files/${fileId}/confirm`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Download package file bytes for Digitize source preview.
 * @returns {Promise<{ blob: Blob, mimeType: string }>}
 */
export async function downloadPackageFileContent(packageId, fileId) {
  const response = await authFetch(
    `${API_BASE_URL}/v1/packages/${packageId}/files/${fileId}/content`,
    {
      method: 'GET',
      headers: {
        ...getContextHeaders(),
      },
    }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    let payload = null;
    try {
      payload = await response.json();
      message = extractErrorMessage(payload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    const code =
      payload && typeof payload === 'object' && typeof payload.code === 'string'
        ? payload.code
        : null;
    throw new ApiError(message, {
      status: response.status,
      code,
      payload,
    });
  }
  const blob = await response.blob();
  const headerType = response.headers.get('content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream').split(';')[0].trim();
  return { blob, mimeType };
}

export function listPackageCopyContexts(packageId) {
  return request(`/v1/packages/${packageId}/copy-contexts`);
}

/**
 * PDF members of an uploaded ZIP, listed server-side. Digitize uses this so a
 * 1,000-PDF batch never has to be downloaded and unzipped in the browser.
 * @returns {Promise<{ items: string[], total: number }>}
 */
export function listPackageFileMembers(packageId, fileId) {
  return request(`/v1/packages/${packageId}/files/${fileId}/members`);
}

export async function downloadPackageFileRendition(
  packageId,
  fileId,
  { disposition = 'inline', member } = {}
) {
  const memberQuery = member ? `&member=${encodeURIComponent(member)}` : '';
  const response = await authFetch(
    `${API_BASE_URL}/v1/packages/${packageId}/files/${fileId}/rendition?disposition=${encodeURIComponent(disposition)}${memberQuery}`,
    {
      method: 'GET',
      headers: {
        ...getContextHeaders(),
      },
    }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    throw new Error(message);
  }
  return readFileResponse(response);
}

export function getOriginatorDocument(assetId) {
  return request(`/v1/originator/documents/${assetId}`);
}

export function getOriginatorExtractedFacts(assetId) {
  return request(`/v1/originator/documents/${assetId}/extracted-facts`);
}

export function getOriginatorEvidenceEvaluation(assetId) {
  return request(`/v1/originator/documents/${assetId}/evidence-evaluation`);
}

/** One page of validation rules (file groups); avoids downloading every rule at once. */
export function getOriginatorEvidencePage(
  assetId,
  { page = 1, pageSize = 50, tab, status, q } = {}
) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (tab) params.set('tab', tab);
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  return request(
    `/v1/originator/documents/${assetId}/evidence-evaluation/page?${params.toString()}`
  );
}

export function getOriginatorEvidenceDetails(assetId, ids) {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (!list.length) return Promise.resolve({ asset_id: assetId, details: {} });
  const params = new URLSearchParams({ ids: list.join(',') });
  return request(
    `/v1/originator/documents/${assetId}/evidence-evaluation/details?${params.toString()}`
  );
}

/**
 * Originator verifies one extracted field.
 */
export function verifyOriginatorFact(assetId, factId, payload = {}) {
  return request(`/v1/originator/documents/${assetId}/facts/${factId}/verify`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Originator verifies many extracted fields (optional documentTypeId).
 */
export function verifyOriginatorFactsBatch(assetId, payload = {}) {
  return request(`/v1/originator/documents/${assetId}/facts/verify-batch`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Available ABDP document types for Digitize classification edits.
 */
export function getOriginatorDocumentTypes(assetId) {
  return request(`/v1/originator/documents/${assetId}/document-types`);
}

/**
 * Change a JD document type via ABDP Process (Originator Digitize).
 */
export function patchOriginatorClassification(assetId, payload) {
  return request(`/v1/originator/documents/${assetId}/classification`, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Correct extracted field values via ABDP field_corrections (Originator Digitize).
 */
export function patchOriginatorExtractedFields(assetId, payload) {
  return request(`/v1/originator/documents/${assetId}/extracted-fields`, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Mark ABDP folder reviewed (Process "Mark as reviewed").
 */
export function markOriginatorReviewed(assetId) {
  return request(`/v1/originator/documents/${assetId}/mark-reviewed`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Download asset package-file bytes for Digitize source preview (asset-scoped).
 * @returns {Promise<{ blob: Blob, mimeType: string }>}
 */
export async function downloadOriginatorDocumentFileContent(assetId, fileId) {
  const response = await authFetch(
    `${API_BASE_URL}/v1/originator/documents/${assetId}/files/${fileId}/content`,
    {
      method: 'GET',
      headers: {
        ...getContextHeaders(),
      },
    }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const headerType = response.headers.get('content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream').split(';')[0].trim();
  return { blob, mimeType };
}

/**
 * Run Digitize VA (classify / extract / validate / tape_validate). Originator only.
 * `tape_validate` compares extracted facts with the package's loan tape locally
 * (no ABDP round trip).
 * @param {'classify'|'extract'|'validate'|'tape_validate'} action
 */
export function runOriginatorVaAction(assetId, action) {
  return request(`/v1/originator/documents/${assetId}/va/${action}`, {
    method: 'POST',
    body: {},
  });
}

export const LOAN_TAPE_ACCEPT = '.csv,.xlsx';

/**
 * Attach (or replace) the package's loan tape (CSV / XLSX) as multipart form data.
 * The browser sets the multipart boundary, so no Content-Type is forced here.
 */
export async function uploadPackageLoanTape(packageId, file) {
  assertNotReadOnly('POST');
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await authFetch(`${API_BASE_URL}/v1/packages/${packageId}/loan-tape`, {
    method: 'POST',
    headers: { ...getContextHeaders() },
    body: form,
  });
  return parseResponse(response);
}

/** Loan tape attached to a package; rejects with a 404 ApiError when none. */
export function getPackageLoanTape(packageId) {
  return request(`/v1/packages/${packageId}/loan-tape`);
}

export function deletePackageLoanTape(packageId) {
  return request(`/v1/packages/${packageId}/loan-tape`, { method: 'DELETE' });
}

/** Loan tape for a Digitize document (asset); 404 when the package has none. */
export function getOriginatorDocumentLoanTape(assetId) {
  return request(`/v1/originator/documents/${assetId}/loan-tape`);
}

export function classifyPackage(packageId, payload) {
  return request(`/v1/packages/${packageId}/classify`, {
    method: 'POST',
    body: payload,
  });
}

export function certifyPackage(packageId, payload = {}) {
  return request(`/v1/packages/${packageId}/certify`, {
    method: 'POST',
    body: payload,
  });
}

export function patchAdmissionFacts(packageId, payload) {
  return request(`/v1/batches/${packageId}/admission-facts`, {
    method: 'PATCH',
    body: payload,
  });
}

/** Batch certification. The server assigns version, actor, and certification_hash. */
export function createPackageCertification(packageId, payload) {
  return request(`/v1/batches/${packageId}/certifications`, {
    method: 'POST',
    body: payload,
  });
}

export function evaluatePackageAdmission(packageId, { rowVersion } = {}) {
  const body = {};
  if (rowVersion !== undefined && rowVersion !== null && rowVersion !== '') {
    body.row_version = rowVersion;
  }
  return request(`/v1/batches/${packageId}/admission-evaluations`, {
    method: 'POST',
    body,
  });
}

export function getPackageAdmission(packageId) {
  return request(`/v1/batches/${packageId}/admission`);
}

export function getDocumentAdmission(documentId) {
  return request(`/v1/documents/${documentId}/admission`);
}

export function listExceptions({
  page = 1,
  pageSize = 20,
  severity,
  status,
  assigneeOrg,
} = {}) {
  return request('/v1/exceptions', {
    query: {
      page,
      page_size: pageSize,
      severity,
      status,
      assignee_org: assigneeOrg,
    },
  });
}

export function cureException(exceptionId, cureNotes) {
  return request(`/v1/exceptions/${exceptionId}/cure`, {
    method: 'POST',
    body: { cure_notes: cureNotes },
  });
}

export function listPrograms() {
  return request('/v1/programs');
}

export function getProgram(programId) {
  return request(`/v1/programs/${programId}`);
}

/* ------------------------------------------------------------------ */
/* Phase 1.1 — Originator portfolio, requests, reports, exports        */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} PortfolioSummaryResponse
 * @property {number} total_batches
 * @property {number} total_loans
 * @property {number} pending_authority
 * @property {number} pending_custody
 */

/**
 * @typedef {Object} PortfolioBatchItem
 * @property {string} batch_id
 * @property {string} program
 * @property {string} submit_date
 * @property {number} loans_count
 * @property {string} authority_state
 * @property {string} custody_facility_state
 * @property {string} next_action
 * @property {string} open_action
 */

/**
 * @typedef {Object} DealFacilitySpvItem
 * @property {string} id
 * @property {string} name
 * @property {string} type
 * @property {string} originator
 * @property {number} loans_count
 * @property {string} current_status
 * @property {string} access_basis
 * @property {string} open_action
 */

/**
 * @typedef {Object} LoanListItem
 * @property {string} id
 * @property {string} loan_id
 * @property {string} borrower
 * @property {string} batch
 * @property {string} deal_facility_spv
 * @property {string} vault_state
 * @property {string} servicing_access
 * @property {string} open_action
 */

/** @returns {Promise<PortfolioSummaryResponse>} */
export function getPortfolioSummary() {
  return request('/v1/originator/portfolio/summary');
}

export function listPortfolioBatches({
  page = 1,
  pageSize = 20,
  search,
  status,
  programId,
  sortBy = 'submit_date',
  sortOrder = 'desc',
} = {}) {
  return request('/v1/originator/portfolio/batches', {
    query: {
      page,
      page_size: pageSize,
      search,
      status,
      program_id: programId,
      sort_by: sortBy,
      sort_order: sortOrder,
    },
  });
}

/** @returns {Promise<DealFacilitySpvItem[]>} */
export function listPortfolioDeals() {
  return request('/v1/originator/portfolio/deals');
}

/**
 * Drill from a deal to its facilities. `dealId` must be a program UUID; the
 * caller is responsible for guarding non-UUID ids before calling this.
 * @returns {Promise<DealFacilitySpvItem[]>}
 */
export function listDealFacilities(dealId) {
  return request(`/v1/originator/portfolio/deals/${dealId}/facilities`);
}

/**
 * `holderId` is the facility's `current_holder` string (e.g. ORIGINATOR),
 * which corresponds to the `id` field returned in the facilities table.
 */
export function getFacilitySummary(holderId) {
  return request(`/v1/originator/portfolio/facilities/${holderId}/summary`);
}

export function listPortfolioLoans({
  page = 1,
  pageSize = 20,
  search,
  authorityState,
  custodyState,
  batchId,
} = {}) {
  return request('/v1/originator/portfolio/loans', {
    query: {
      page,
      page_size: pageSize,
      search,
      authority_state: authorityState,
      custody_state: custodyState,
      batch_id: batchId,
    },
  });
}

export function getPortfolioLoan(loanId) {
  return request(`/v1/originator/portfolio/loans/${loanId}`);
}

export function getLoanCustodyState(loanId) {
  return request(`/v1/originator/portfolio/loans/${loanId}/custody-state`);
}

export function getLoanEvidence(loanId) {
  return request(`/v1/originator/portfolio/loans/${loanId}/evidence`);
}

/**
 * @typedef {Object} AssetRequestResponse
 * @property {string} id
 * @property {string} asset_id
 * @property {string} request_type
 * @property {string} status
 * @property {string} idempotency_key
 * @property {string | null} notes
 * @property {string} created_at
 * @property {string} updated_at
 */

export function listRequests({ page = 1, pageSize = 20, status, requestType } = {}) {
  return request('/v1/originator/requests', {
    query: {
      page,
      page_size: pageSize,
      status,
      request_type: requestType,
    },
  });
}

// Internal: POST an asset request. `idempotencyKey` is generated once per
// submit intent by the caller and reused across retries.
function createAssetRequest(action, assetId, payload, idempotencyKey) {
  return request(`/v1/originator/assets/${assetId}/${action}`, {
    method: 'POST',
    body: {
      notes: payload?.notes ?? null,
      purpose: payload?.purpose ?? null,
      delivery_channel: payload?.delivery_channel ?? null,
    },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/** @returns {Promise<AssetRequestResponse>} */
export function createPledgeRequest(assetId, payload, idempotencyKey) {
  return createAssetRequest('pledge', assetId, payload, idempotencyKey);
}

/** @returns {Promise<AssetRequestResponse>} */
export function createReleaseRequest(assetId, payload, idempotencyKey) {
  return createAssetRequest('release', assetId, payload, idempotencyKey);
}

/** @returns {Promise<AssetRequestResponse>} */
export function createTransferRequest(assetId, payload, idempotencyKey) {
  return createAssetRequest('transfer', assetId, payload, idempotencyKey);
}

/** @returns {Promise<AssetRequestResponse>} */
export function createCopyRequest(assetId, payload, idempotencyKey) {
  return createAssetRequest('copy-requests', assetId, payload, idempotencyKey);
}

/**
 * @typedef {Object} ReportTileSummaryItem
 * @property {string} tile_key
 * @property {string} title
 * @property {number} count
 */

/**
 * @typedef {Object} DashboardPayload
 * @property {Record<string, string>} filters
 * @property {{ key: string, label: string, value: number }[]} metrics
 * @property {{ label: string, value: number }[]} trend
 * @property {{ key: string, value: string }[]} detail_grid
 */

export function getReportsSummary() {
  return request('/v1/originator/reports/summary');
}

/** @returns {Promise<DashboardPayload>} */
export function getBoardingSummary() {
  return request('/v1/originator/reports/boarding-summary');
}

/** @returns {Promise<DashboardPayload>} */
export function getAuthorityOutcomes() {
  return request('/v1/originator/reports/authority-outcomes');
}

/** @returns {Promise<DashboardPayload>} */
export function getFacilityStatus() {
  return request('/v1/originator/reports/facility-status');
}

/** @returns {Promise<DashboardPayload>} */
export function getSpvSoldServicing() {
  return request('/v1/originator/reports/spv-sold-servicing');
}

/** @returns {Promise<DashboardPayload>} */
export function getEvidenceExportHistory() {
  return request('/v1/originator/reports/evidence-export-history');
}

/**
 * @typedef {Object} ExportListItem
 * @property {string} id
 * @property {string} export_type
 * @property {string} status
 * @property {string | null} error_message
 * @property {string} created_at
 * @property {string} updated_at
 */

export function listExports({ page = 1, pageSize = 20 } = {}) {
  return request('/v1/originator/exports', {
    query: { page, page_size: pageSize },
  });
}

export function reExport(exportId) {
  return request(`/v1/originator/exports/${exportId}/re-export`, {
    method: 'POST',
  });
}
